import { OnboardingFlowStage } from '../enums';
import { ProcessingTicketingDataState } from '../graphql';
import { useFiscalYearOrFail } from './useFiscalYear';
import { useOnboardingFlowCompletionPercent } from './useOnboardingFlowCompletionPercent';
import { useSelectedFiscalYearId } from './useSelectedFiscalYearId';

type UseOnboardingFlowStage = {
  startPolling: (pollIntervalMs: number) => void;
  stopPolling: () => void;
} & (
  | {
      loading: true;
      stage: null;
    }
  | {
      loading: false;
      stage: OnboardingFlowStage;
    }
);

export const useOnboardingFlowStage = (): UseOnboardingFlowStage => {
  const fiscalYearId = useSelectedFiscalYearId();
  const {
    fiscalYear,
    startPolling: fiscalYearStartPolling,
    stopPolling: fiscalYearStopPolling,
  } = useFiscalYearOrFail({ fiscalYearId });
  const {
    loading: loadingPercentCompleted,
    percentCompleted,
    startPolling: percentStartPolling,
    stopPolling: percentStopPolling,
  } = useOnboardingFlowCompletionPercent();

  const baseResult = {
    startPolling: percentCompleted === 100 ? fiscalYearStartPolling : percentStartPolling,
    stopPolling: percentCompleted === 100 ? fiscalYearStopPolling : percentStopPolling,
  };

  if (loadingPercentCompleted) {
    return {
      ...baseResult,
      loading: true,
      stage: null,
    };
  }

  if (
    fiscalYear.processingTicketingDataState === ProcessingTicketingDataState.Skipped ||
    fiscalYear.processingTicketingDataState === ProcessingTicketingDataState.Completed
  ) {
    return {
      ...baseResult,
      loading: false,
      stage: OnboardingFlowStage.DATA_PROCESSING_COMPLETED,
    };
  }

  if (fiscalYear.processingTicketingDataState === ProcessingTicketingDataState.InProgress) {
    return {
      ...baseResult,
      loading: false,
      stage: OnboardingFlowStage.DATA_PROCESSING_IN_PROGRESS,
    };
  }

  return {
    ...baseResult,
    loading: false,
    stage: OnboardingFlowStage.INITIAL,
  };
};
